/**
 * RolesPermissions — migrated from mock to live systemRolesApi.
 *
 * CRUD for tenant-scoped roles. System (built-in) roles are read-only;
 * custom roles support create/edit/delete. Permissions are fetched from
 * systemPermissionsApi and rendered as a multi-select in the form.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel,
  ListItemText, MenuItem, OutlinedInput, Paper, Select, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, RefreshOutlined, EditOutlined, DeleteOutlined, VpnKeyOutlined } from '@mui/icons-material';
import { ApiError, systemApi, type Schema } from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';
import {
  getPermissionDescription,
  getPermissionModuleLabel,
  getRoleDescription,
  getRoleName,
} from './rolesPermissionsMeta';

type Role = Schema<'RoleResponse'>;
type Permission = Schema<'PermissionResponse'>;
type ActiveDialog =
  | { kind: 'form'; role: Role | null }
  | { kind: 'viewRole'; role: Role }
  | { kind: 'permissions' };

interface FormState {
  name: string;
  code: string;
  description: string;
  permissions: string[];
}

const EMPTY_FORM: FormState = { name: '', code: '', description: '', permissions: [] };

const RolesPermissions: React.FC = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const editing = activeDialog?.kind === 'form' ? activeDialog.role : null;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [toDelete, setToDelete] = useState<Role | null>(null);

  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        systemApi.systemRolesApi.list() as Promise<Role[]>,
        systemApi.systemPermissionsApi.list() as Promise<Permission[]>,
      ]);
      setRoles(r);
      setPermissions(p);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:accessControl.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filter + paginate. Built-in roles are searched by their localized stable-code labels.
  const filtered = useMemo(() => {
    return roles.filter(r => {
      if (typeFilter === 'builtin' && !r.is_system) return false;
      if (typeFilter === 'custom' && r.is_system) return false;
      const q = filter.toLowerCase();
      if (!q) return true;
      return getRoleName(r, t).toLowerCase().includes(q)
        || getRoleDescription(r, t).toLowerCase().includes(q)
        || r.code.toLowerCase().includes(q);
    });
  }, [roles, filter, typeFilter, t]);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // One discriminated state drives the single workflow Dialog.
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setActiveDialog({ kind: 'form', role: null });
  };
  const openEdit = (r: Role) => {
    setForm({ name: r.name, code: r.code, description: r.description ?? '', permissions: r.permissions ?? [] });
    setFormError(null);
    setActiveDialog({ kind: 'form', role: r });
  };
  const closeDialog = () => { setActiveDialog(null); setFormError(null); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError(t('system:accessControl.nameRequired')); return; }
    if (!editing && !form.code.trim()) { setFormError(t('system:accessControl.codeRequired')); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await systemApi.systemRolesApi.update(editing.id, {
          name: form.name, description: form.description || null, permissions: form.permissions,
        });
        setToast(t('system:rolesPermissions.toastUpdated', { name: form.name }));
      } else {
        await systemApi.systemRolesApi.create({
          name: form.name, code: form.code, description: form.description || null, permissions: form.permissions,
        });
        setToast(t('system:rolesPermissions.toastCreated', { name: form.name }));
      }
      closeDialog();
      await fetchAll();
    } catch (e) {
      setFormError(e instanceof ApiError ? translateApiError(e, t) : t('system:accessControl.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await systemApi.systemRolesApi.delete(toDelete.id);
      setToast(t('system:rolesPermissions.toastDeleted', { name: toDelete.name }));
      setToDelete(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:accessControl.deleteFailed'));
      throw e;
    }
  };

  // Permission modules for grouped display
  const permModules = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of permissions) {
      const mod = p.module ?? 'other';
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod)!.push(p);
    }
    return map;
  }, [permissions]);

  const permissionByCode = useMemo(
    () => new Map(permissions.map(permission => [permission.code, permission])),
    [permissions],
  );

  if (loading && roles.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error && roles.length === 0) {
    return <Box sx={{ py: 4 }}><Alert severity="error" action={<Button onClick={fetchAll} size="small">{t('common:actions.retry')}</Button>}>{error}</Alert></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:accessControl.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:accessControl.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchAll} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="outlined" size="small" startIcon={<VpnKeyOutlined />} onClick={() => setActiveDialog({ kind: 'permissions' })} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#fff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#fff' } }}>{t('system:accessControl.viewAllPermissions')}</Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('system:accessControl.addRole')}</Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('system:accessControl.typeLabel')}</InputLabel>
          <Select value={typeFilter} label={t('system:accessControl.typeLabel')} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('system:accessControl.allRoles')}</MenuItem>
            <MenuItem value="builtin">{t('system:accessControl.builtIn')}</MenuItem>
            <MenuItem value="custom">{t('system:accessControl.custom')}</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label={t('common:actions.search')} value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }} sx={{ width: 280 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[5, 10, 25]} sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }} />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('system:accessControl.colRole', { defaultValue: 'Role' })}</TableCell>
              <TableCell>{t('system:accessControl.colDescription', { defaultValue: 'Description' })}</TableCell>
              <TableCell>{t('system:accessControl.colPermissions', { defaultValue: 'Permissions' })}</TableCell>
              <TableCell align="center">{t('system:accessControl.colActions', { defaultValue: 'Actions' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">{t('system:accessControl.noRolesFound')}</Typography>
              </TableCell></TableRow>
            ) : paginated.map(r => {
              const displayName = getRoleName(r, t);
              const displayDescription = getRoleDescription(r, t);
              return (
              <TableRow key={r.id} hover onClick={() => setActiveDialog({ kind: 'viewRole', role: r })} sx={{ cursor: 'pointer' }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                  {/* The badge sits on the SECOND line, before the code, so its left edge is the
                      cell's content origin on every row. Trailing the name put it at
                      name_x + name_width + 8px, and the names differ ("Admin" vs "Super Admin"),
                      so by construction the badges could never line up — measured as a 44px
                      sawtooth down the column. Vertical centring inside the chip was already
                      correct (4px above / 4px below an 18px box, measured); the defect was
                      horizontal. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                    {r.is_system && (
                      <Chip
                        label={t('system:accessControl.builtIn')}
                        size="small"
                        sx={{ borderRadius: 1, height: 16, fontSize: '0.6rem',
                              '& .MuiChip-label': { px: 0.625 } }}
                      />
                    )}
                    <Typography variant="caption" color="text.disabled">{r.code}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{displayDescription}</Typography></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(r.permissions ?? []).slice(0, 3).map(code => {
                      const permission = permissionByCode.get(code);
                      return (
                        <Chip
                          key={code}
                          label={permission ? getPermissionDescription(permission, t) : t('system:accessControl.unknownPermission')}
                          title={code}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }}
                        />
                      );
                    })}
                    {(r.permissions ?? []).length > 3 && <Chip label={`+${(r.permissions ?? []).length - 3}`} size="small" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />}
                    {(r.permissions ?? []).length === 0 && r.code !== 'super_admin' && <Typography variant="caption" color="text.disabled">{t('common:feedback.none')}</Typography>}
                    {r.code === 'super_admin' && <Chip label={t('system:accessControl.allPerms')} size="small" color="error" variant="outlined" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    {!r.is_system && <IconButton size="small" onClick={(event) => { event.stopPropagation(); openEdit(r); }}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>}
                    {!r.is_system && <IconButton size="small" onClick={(event) => { event.stopPropagation(); setToDelete(r); }} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>}
                    {r.is_system && <Typography variant="caption" color="text.disabled">—</Typography>}
                  </Stack>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* One state-driven Dialog handles create/edit, role detail, and permission catalog. */}
      <Dialog open={!!activeDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        {activeDialog?.kind === 'form' && (
          <>
            <DialogTitle sx={{ py: 2 }}>
              {editing
                ? t('system:accessControl.editRole', { name: getRoleName(editing, t) })
                : t('system:accessControl.addNewRole')}
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={t('system:accessControl.nameLabel')} required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={t('system:accessControl.codeLabel')} required disabled={!!editing} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} helperText={editing ? t('system:accessControl.codeHelperEdit') : t('system:accessControl.codeHelperNew')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label={t('system:accessControl.descriptionLabel')} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('system:accessControl.permissionsLabel')}</InputLabel>
                    <Select
                      multiple
                      value={form.permissions}
                      onChange={e => setForm(f => ({ ...f, permissions: e.target.value as string[] }))}
                      input={<OutlinedInput label={t('system:accessControl.permissionsLabel')} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.slice(0, 5).map(code => {
                            const permission = permissionByCode.get(code);
                            return <Chip key={code} label={permission ? getPermissionDescription(permission, t) : t('system:accessControl.unknownPermission')} title={code} size="small" sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }} />;
                          })}
                          {selected.length > 5 && <Chip label={`+${selected.length - 5}`} size="small" sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }} />}
                        </Box>
                      )}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      {Array.from(permModules.entries()).map(([mod, perms]) => [
                        <MenuItem key={`header-${mod}`} disabled sx={{ opacity: 1, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', py: 0.5 }}>
                          {getPermissionModuleLabel(mod, t)}
                        </MenuItem>,
                        ...perms.map(permission => (
                          <MenuItem key={permission.code} value={permission.code} sx={{ py: 0.25 }}>
                            <Checkbox checked={form.permissions.includes(permission.code)} size="small" sx={{ p: 0.5 }} />
                            <ListItemText
                              primary={getPermissionDescription(permission, t)}
                              secondary={permission.code}
                              primaryTypographyProps={{ fontSize: '0.8rem' }}
                              secondaryTypographyProps={{ fontSize: '0.65rem', fontFamily: 'monospace' }}
                            />
                          </MenuItem>
                        )),
                      ]).flat()}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
                {saving ? <CircularProgress size={18} /> : editing ? t('common:actions.save') : t('system:accessControl.createRole')}
              </Button>
            </DialogActions>
          </>
        )}

        {activeDialog?.kind === 'viewRole' && (
          <>
            <DialogTitle sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>{getRoleName(activeDialog.role, t)}</Typography>
              {activeDialog.role.is_system && <Chip label={t('system:accessControl.builtIn')} size="small" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>{activeDialog.role.code}</Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getRoleDescription(activeDialog.role, t)}
              </Typography>
              {activeDialog.role.code === 'super_admin' ? (
                <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                  {t('system:accessControl.superAdminAccess', { count: permissions.length })}
                </Alert>
              ) : (activeDialog.role.permissions ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('system:accessControl.noExplicitPermissions')}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {t('system:accessControl.assignedCount', { count: (activeDialog.role.permissions ?? []).length })}
                  </Typography>
                  {Array.from(permModules.entries()).map(([mod, perms]) => {
                    const assigned = perms.filter(permission => (activeDialog.role.permissions ?? []).includes(permission.code));
                    if (assigned.length === 0) return null;
                    return (
                      <Box key={mod} sx={{ mb: 1.5 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.65rem', letterSpacing: 0.5 }}>
                          {getPermissionModuleLabel(mod, t)}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                          {assigned.map(permission => (
                            <Chip key={permission.code} label={getPermissionDescription(permission, t)} title={permission.code} size="small" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              {!activeDialog.role.is_system && <Button onClick={() => openEdit(activeDialog.role)} sx={{ textTransform: 'none' }}>{t('common:actions.edit')}</Button>}
              <Button onClick={closeDialog} variant="contained" sx={{ textTransform: 'none' }}>{t('common:actions.close')}</Button>
            </DialogActions>
          </>
        )}

        {activeDialog?.kind === 'permissions' && (
          <>
            <DialogTitle sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKeyOutlined color="primary" />
              <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>{t('system:accessControl.allPermissionsTitle')}</Typography>
              <Chip label={t('system:accessControl.totalPermissions', { count: permissions.length })} size="small" sx={{ ml: 'auto', borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Alert severity="info" variant="outlined" sx={{ mb: 2, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0.5 } }}>
                {t('system:accessControl.permissionsCodeDefined')}
              </Alert>
              {Array.from(permModules.entries()).map(([mod, perms]) => (
                <Box key={mod} sx={{ mb: 2 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5 }}>
                    {getPermissionModuleLabel(mod, t)} <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>({perms.length})</Typography>
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    {perms.map(permission => (
                      <Box key={permission.code} sx={{ py: 0.25 }}>
                        <Typography variant="caption" color="text.primary" sx={{ display: 'block', lineHeight: 1.4 }}>
                          {getPermissionDescription(permission, t)}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.62rem' }}>
                          {permission.code}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </DialogContent>
            <DialogActions sx={{ py: 2 }}>
              <Button onClick={closeDialog} variant="contained" sx={{ textTransform: 'none' }}>{t('common:actions.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete remains a dedicated destructive confirmation primitive. */}
      <ConfirmDialog
        open={!!toDelete}
        tone="danger"
        title={t('system:accessControl.deleteTitle')}
        description={t('system:accessControl.deleteDescription')}
        subject={toDelete && <Typography sx={{ fontWeight: 600 }}>{toDelete.name} <Typography component="span" color="text.secondary">({toDelete.code})</Typography></Typography>}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RolesPermissions;
